import { RecordMap } from "@/Shared/Helpers";
import { AuditModel } from "../Models";
import { useUser } from "@/OneLiners";

/**
 * useAuditEnricher hook – aggregates and enriches audit-related fields for any AuditModel entity.
 * 
 * 
 * Responsibilities:
 * - Fetches users data for agent resolution
 * - Enriches audit fields (createdBy, updatedBy, deletedBy, restoredBy) with full user objects
 * - Provides utility methods to enrich single models or lists
 * - Combines async status and errors into a unified response
 * 
 * Behavior:
 * - Safely handles missing users
 * - Works generically for any model extending AuditModel
 * - Exposes enriched data and user list for downstream hooks or UI components
 * 
 * Author: Samano CASTRE
 * Date: January 29, 2026
 */
export function useAuditEnricher<T extends AuditModel>() {

    /**
     * Users data – used for resolving audit fields to full user objects
     */
    const {
        dataMap: userMap,
        stateResponse,
    } = useUser();

    /**
    * Enriches a list of models with audit users
    * - Maps each model through getEnrichedModel
    */
    const getEnrichedAuditMap = (modelMap: RecordMap<T>): RecordMap<T> => {
        return modelMap.map<T>(getEnrichedAudit);
    }

    /**
     * Enriches a single model with audit users
     * - Resolves createdBy, updatedBy, deletedBy, restoredBy fields
     * - Returns a new model instance with enriched fields
     */
    const getEnrichedAudit = (model: T): T => {
        if (!userMap || !model) {
            return model;
        }
        return {
            ...model,
            createdByUser: userMap.get(model.createdBy),
            updatedByUser: model.updatedBy ? userMap.get(model.updatedBy) : undefined,
            deletedByUser: model.deletedBy ? userMap.get(model.deletedBy) : undefined,
            restoredByUser: model.restoredBy ? userMap.get(model.restoredBy) : undefined,
        } as T;
    }

    const getEnrichedUserById = (id: number): T | undefined => {
        const user = userMap.get(id);
        return user
            ? getEnrichedAudit(user as unknown as T)
            : undefined;
    }

    const getUserById = (id?: number | null) => {
        if (!id || !userMap) return undefined;
        return userMap.get(id);
    }


    /**
     * Public API – exposed to consumers of the hook
     */
    return {
        userMap,
        getEnrichedAuditMap,
        getEnrichedAudit,
        getEnrichedUserById,
        getUserById,
        enricherResponse: stateResponse
    }
}
